import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Badge } from "@zkt/components";
import { fromEvent } from "rxjs";
import { merchant$ } from "@zkt/eb-utility";
import { Search } from "@zkt/icons";
import { Menu as MenuModel } from "./model";
import "./menu.scss";

const MenuContext = React.createContext<{
  messageCount: Record<string, number>;
}>(null);

function HighlightText(props: { text: string; keyword: string }) {
  const { text, keyword } = props;
  return useMemo(() => {
    if (keyword && text?.includes(keyword)) {
      const index = text.indexOf(keyword);
      return (
        <>
          {text.substring(0, index)}
          <mark>{keyword}</mark>
          {text.substring(index + keyword.length)}
        </>
      );
    }
    return <>{text}</>;
  }, [text, keyword]);
}

function formatLink(link: string) {
  if (link?.includes("CURRENT_HOTEL_ID")) {
    return link.replace(
      /CURRENT_HOTEL_ID/g,
      String(merchant$.value?.merchantId)
    );
  }
  return link || undefined;
}

function MenuItem(props: MenuModel) {
  const {
    menuExtInfo,
    name,
    subTitle,
    children,
    link,
    target,
    parentIds,
    ...rest
  } = props;
  const { messageCount } = useContext(MenuContext);

  const totalCount = useMemo(() => {
    if (!messageCount) {
      return 0;
    }
    const getCount = function (m) {
      if (m.messagekey === "pending_orders") {
        // eslint-disable-next-line no-underscore-dangle
        return messageCount.__room_orders || messageCount[m.messagekey] || 0;
      }
      if (m.messagekey === "brand_pending_orders") {
        return (
          // eslint-disable-next-line no-underscore-dangle
          messageCount.__brand_room_orders || messageCount[m.messagekey] || 0
        );
      }
      return messageCount[m.messagekey] || 0;
    };
    const sum = (m) => m.children.reduce((r, x) => r + sum(x), getCount(m));

    // const menu = (m) =>
    //   m.children.reduce((r, x) => {
    //     if (!x.menuExtInfo) {
    //       menu(x);
    //     } else {
    //       // eslint-disable-next-line no-param-reassign
    //       r = x.menuExtInfo;
    //     }
    //     return r;
    //   }, m.menuExtInfo);
    // function getMenuPopText(prop) {
    //   const totalSum = sum(prop); // 计算总和（假设sum函数已定义）
    //   const menuInfo = menu(prop); // 获取menu信息

    //   if (!totalSum && menuInfo && menuInfo.popText) {
    //     // 如果menu信息存在且包含popText属性，则返回popText属性的值
    //     return menuInfo.popText;
    //   }
    //   // 如果以上条件都不满足，则返回null或者其他你希望返回的默认值
    //   return totalSum;
    // }

    return sum(props);
  }, [messageCount, props]);

  // 气泡组件
  const bubbleCount = useMemo(() => {
    if (menuExtInfo) {
      return (
        <div className="pop-text" title={menuExtInfo.popText}>
          <div>{menuExtInfo.popText}</div>
        </div>
      );
    }
    return 0;
  }, [menuExtInfo]);

  // 做兼容(因为数字是有其他条件)
  const isTotalCount = useMemo(() => {
    return +totalCount || bubbleCount;
  }, [bubbleCount, totalCount]);

  const CurrentBadge = (
    <div className="menu-text" data-parent={parentIds}>
      <Badge size="small" count={isTotalCount}>
        {name}
      </Badge>
      {subTitle && <small>{subTitle}</small>}
    </div>
  );
  if (children?.length) {
    return (
      <Menu.SubMenu {...rest} title={CurrentBadge}>
        {children.map((c) => {
          return <MenuItem key={c.id} {...c} />;
        })}
      </Menu.SubMenu>
    );
  }
  if (link) {
    return (
      <Menu.Item {...rest}>
        <Link target={target} to={formatLink(link)}>
          {CurrentBadge}
        </Link>
      </Menu.Item>
    );
  }
  return null;
}

function filterByKeyword(items: Array<MenuModel>, keyword: string) {
  if (!keyword || !items) return items;
  const contains = (x) =>
    (x.name && x.name.includes(keyword)) || x.children.some(contains);
  return items
    .filter((x) => contains(x))
    .map((x) => ({
      ...x,
      name: <HighlightText keyword={keyword} text={x.name as string} />,
    }));
}

export default function InnerMenu(props: {
  keyword: string;
  items: Array<MenuModel>;
  messageCount: Record<string, number>;
  inlineCollapsed: boolean;
  onCollapsed: (v: boolean) => void;
  onClearKeyword: () => void;
}): JSX.Element {
  const {
    keyword,
    items,
    messageCount,
    inlineCollapsed,
    onCollapsed,
    onClearKeyword,
  } = props;

  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [lastestItem, setLatestItem] = useState<MenuModel>();
  const [searchResult, setSearchResult] = useState<Array<MenuModel>>();
  const [searchOpenKeys, setSearchOpenKeys] = useState<Array<string>>();

  const location = useLocation();

  /**
   * 页面跳转时同步更新菜单选中和展开状态
   */
  const syncMenuWithUrlChange = useCallback(
    ({ hash, pathname }) => {
      // 监听url变化，修改菜单的选中和展示状态
      let path = `/${hash || pathname}`.replace(/\/+/, "/");
      path = path === "/" ? "/#/dashboard" : path;
      path = decodeURIComponent(path);
      if (path === lastestItem?.link) return;
      const menu = MenuModel.find(items, (m) => m.link === path);
      setLatestItem(menu);
      if (menu) {
        setSelectedKeys([menu.id]);
        setOpenKeys(menu.parentIds);
      }
    },
    [lastestItem, items]
  );

  useEffect(() => {
    syncMenuWithUrlChange(location);
  }, [syncMenuWithUrlChange, location]);

  useEffect(() => {
    const subscription = fromEvent(window, "hashchange").subscribe(() => {
      syncMenuWithUrlChange(window.location);
    });
    return () => subscription.unsubscribe();
  }, [syncMenuWithUrlChange]);

  const handleMenuOpenChange = useCallback(
    (keys: Array<string>) => {
      setOpenKeys(keys);
      if (keys.length && inlineCollapsed) {
        // 展开和收起时也会触发菜单OpenChange事件
        // 加一个延迟操作，防止和展开和收起按钮事件冲突
        setTimeout(() => {
          if (inlineCollapsed) {
            onCollapsed(false);
          }
        }, 100);
      }
    },
    [inlineCollapsed, onCollapsed]
  );
  const handleMenuClick = useCallback(
    ({ key }) => {
      const x = MenuModel.find(items, (m) => m.id === key);
      setSelectedKeys([key]);
      setOpenKeys(x.parentIds);
      setLatestItem(x);
      if (inlineCollapsed) {
        onCollapsed(false);
      }
    },
    [inlineCollapsed, onCollapsed, items]
  );

  // 搜索
  useEffect(() => {
    const keys = new Set<string>();
    const filterRecursively = (_menus: Array<MenuModel>) => {
      const result = filterByKeyword(_menus, keyword);
      result.forEach((x) => {
        const children = filterRecursively(x.children);
        keys.add(x.id);
        Object.assign(x, {
          children: children?.length ? children : x.children,
        });
      });
      return result;
    };
    if (keyword) {
      const result = filterRecursively(items);
      setSearchOpenKeys(Array.from(keys));
      setSearchResult(result);
    } else {
      setSearchResult(undefined);
    }
  }, [keyword, items]);
  return (
    <MenuContext.Provider value={{ messageCount }}>
      <Menu
        className={keyword?.length ? "d-none" : ""}
        inlineIndent={17}
        mode="inline"
        theme="dark"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onClick={handleMenuClick}
        onOpenChange={handleMenuOpenChange}
        inlineCollapsed={inlineCollapsed}
        triggerSubMenuAction="click"
        getPopupContainer={() => null}
      >
        {items?.map((m) => {
          return <MenuItem key={m.id} {...m} />;
        })}
      </Menu>
      {!!searchResult?.length && (
        <Menu
          inlineIndent={17}
          mode="inline"
          theme="dark"
          expandIcon={<span />}
          openKeys={searchOpenKeys}
          onClick={onClearKeyword}
        >
          {searchResult?.map((m) => {
            return <MenuItem key={m.id} {...m} />;
          })}
        </Menu>
      )}
      {!!(keyword && !searchResult?.length) && (
        <div className="empty-container">
          <Search width={30} height={31} />
          <div>没有相关菜单哦~</div>
          <div>换个条件试试~</div>
        </div>
      )}
    </MenuContext.Provider>
  );
}
